import { ApiError, api, q } from "@/lib/api";
import { getToken, useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import type { EmulatorApp, EmulatorSessionDTO, FormFactor } from "@mm-sales/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

/**
 * Live devices for a rep: the api restores a golden snapshot on the emulator VM, logs the
 * demo account into the chosen app and streams it (ws-scrcpy) into a frame. A page can run
 * several at once — the phone in Mobile App, the register/kiosk/handheld/CFD on the hardware
 * floor — each frame owning one session by `purpose`. Heart-beats every 20 s; releases on
 * leave (unmount ends outright; tab close / reload stamps it and the api frees it after a
 * short grace unless the page comes back and heart-beats).
 */
export type DeviceMeta = {
  app: EmulatorApp;
  label: string;
  hint: string;
  frame: FormFactor;
};
export const DEVICES: DeviceMeta[] = [
  {
    app: "pos",
    label: "SUNMI POS",
    hint: "Tablet · orders, tables, kitchen",
    frame: "tablet",
  },
  {
    app: "admin",
    label: "Admin app",
    hint: "Phone · the owner's dashboard",
    frame: "phone",
  },
  {
    app: "client",
    label: "Connect app",
    hint: "Phone · MyManager Connect",
    frame: "phone",
  },
  {
    app: "whitelabel",
    label: "White label app",
    hint: "Phone · their own branded app",
    frame: "phone",
  },
];
export const deviceFor = (app: EmulatorApp | undefined) => DEVICES.find((d) => d.app === app);

export type LaunchOpts = {
  purpose: string;
  app: EmulatorApp;
  deviceCode?: string | null;
  formFactor?: FormFactor | null;
  /** Register only: the same emulator also gets a customer-facing display (see CfdVideo). */
  cfd?: boolean;
};

/** Sessions from before purposes existed: the POS was the hardware tab, everything else the phone. */
export const purposeOf = (s: EmulatorSessionDTO) =>
  s.purpose ?? (s.app === "pos" ? "pos" : "phone");

type Ctx = {
  /** False when nobody is signed in as a rep: the portal shows mocks instead. */
  enabled: boolean;
  sessions: EmulatorSessionDTO[];
  /** Purposes with a start / switch / end in flight. */
  pending: ReadonlySet<string>;
  error: string | null;
  capacity: { total: number; free: number } | null;
  launch: (opts: LaunchOpts) => void;
  end: (purpose: string) => void;
};
const LiveDeviceCtx = createContext<Ctx>({
  enabled: false,
  sessions: [],
  pending: new Set(),
  error: null,
  capacity: null,
  launch: () => {},
  end: () => {},
});
export const useLiveDevices = () => useContext(LiveDeviceCtx);

/** One frame's view: its session (if any), and start / switch / end scoped to that purpose. */
export function useLiveSlot(purpose: string) {
  const c = useContext(LiveDeviceCtx);
  const session = c.sessions.find((s) => purposeOf(s) === purpose) ?? null;
  return {
    enabled: c.enabled,
    session,
    busy: c.pending.has(purpose),
    /** Something else on the page is starting — capacity may be taken meanwhile. */
    anyBusy: c.pending.size > 0,
    error: c.error,
    capacity: c.capacity,
    launch: (app: EmulatorApp, extra?: Omit<LaunchOpts, "purpose" | "app">) =>
      c.launch({ purpose, app, ...extra }),
    end: () => c.end(purpose),
  };
}

export function LiveDeviceProvider({
  demoId,
  children,
}: {
  demoId: string | null;
  children: ReactNode;
}) {
  const { token, rejectToken } = useAuth();
  const qc = useQueryClient();
  const me = useQuery({
    queryKey: ["me", token],
    queryFn: q.me,
    enabled: Boolean(token),
    retry: false,
  });
  // The lent token expires (30 min): drop it and ask the studio tab for a fresh one
  // instead of silently turning into the prospect view.
  const meUnauthorized = me.isError && (me.error as { status?: number }).status === 401;
  useEffect(() => {
    if (meUnauthorized) rejectToken();
  }, [meUnauthorized, rejectToken]);
  const enabled = Boolean(token && me.data && demoId);
  const [sessions, setSessions] = useState<EmulatorSessionDTO[]>([]);
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const mark = (purpose: string, on: boolean) =>
    setPending((p) => {
      const n = new Set(p);
      if (on) n.add(purpose);
      else n.delete(purpose);
      return n;
    });

  const capacity = useQuery({
    queryKey: ["capacity"],
    queryFn: q.capacity,
    enabled,
    refetchInterval: 15_000,
  });
  const active = useQuery({
    queryKey: ["sessions", "active"],
    queryFn: q.activeSessions,
    enabled,
  });
  // Resume this demo's ready sessions (a reload, or a second tab) into their frames.
  useEffect(() => {
    const mine = (active.data?.items ?? []).filter(
      (s) => s.status === "ready" && s.demoId === demoId,
    );
    if (!mine.length) return;
    setSessions((cur) => {
      const taken = new Set(cur.map(purposeOf));
      const add = mine.filter((s) => !cur.some((c) => c.id === s.id) && !taken.has(purposeOf(s)));
      return add.length ? [...cur, ...add] : cur;
    });
  }, [active.data, demoId]);
  // Forget a session the api says is over, and scrub it from the cached "active" list so the
  // resume effect can't put it straight back. (It did: a dead session bounced between the
  // heartbeat and that cache every 20 s, remounting the stream each time — the "flashing".)
  const dropSession = useCallback(
    (id: string) => {
      setSessions((cur) => cur.filter((s) => s.id !== id));
      qc.setQueryData<{ items: EmulatorSessionDTO[] }>(["sessions", "active"], (d) =>
        d ? { items: d.items.filter((s) => s.id !== id) } : d,
      );
      qc.invalidateQueries({ queryKey: ["sessions", "active"] });
      qc.invalidateQueries({ queryKey: ["capacity"] });
    },
    [qc],
  );
  const isGone = (e: unknown) =>
    e instanceof ApiError && (e.status === 410 || e.status === 404 || /isn't live/.test(e.message));

  const sessionsRef = useRef<EmulatorSessionDTO[]>([]);
  sessionsRef.current = sessions;

  const launch = useCallback(
    (opts: LaunchOpts) => {
      if (!enabled) return;
      const cur = sessionsRef.current.find((s) => purposeOf(s) === opts.purpose) ?? null;
      if (cur && cur.app === opts.app) return;
      mark(opts.purpose, true);
      setError(null);
      const req = cur
        ? // Same device, next app: no snapshot restore, no second slot.
          api.post<EmulatorSessionDTO>(`/v1/sessions/${cur.id}/app`, { app: opts.app })
        : api.post<EmulatorSessionDTO>("/v1/sessions", {
            demoId,
            app: opts.app,
            purpose: opts.purpose,
            deviceCode: opts.deviceCode ?? undefined,
            formFactor: opts.formFactor ?? undefined,
            cfd: opts.cfd || undefined,
          });
      req
        .then((s) => {
          setSessions((list) =>
            cur
              ? list.map((x) => (x.id === cur.id ? s : x))
              : [...list.filter((x) => purposeOf(x) !== opts.purpose), s],
          );
          qc.invalidateQueries({ queryKey: ["capacity"] });
        })
        .catch((e: Error) => {
          setError(e.message);
          if (cur && isGone(e)) dropSession(cur.id);
        })
        .finally(() => mark(opts.purpose, false));
    },
    [enabled, demoId, qc, dropSession],
  );
  const end = useCallback(
    (purpose: string) => {
      const cur = sessionsRef.current.find((s) => purposeOf(s) === purpose);
      if (!cur) return;
      mark(purpose, true);
      api
        .del(`/v1/sessions/${cur.id}`)
        .catch(() => {})
        .finally(() => {
          dropSession(cur.id);
          mark(purpose, false);
        });
    },
    [dropSession],
  );

  // Heart-beat every ready session; a beat right away when the set changes so a reloaded
  // page clears its /leave stamp inside the grace.
  const readyIds = sessions
    .filter((s) => s.status === "ready")
    .map((s) => s.id)
    .join(",");
  useEffect(() => {
    if (!readyIds) return;
    const ids = readyIds.split(",");
    const beat = () =>
      ids.forEach((id) =>
        api
          .post<EmulatorSessionDTO>(`/v1/sessions/${id}/heartbeat`)
          .then((s) => s.status !== "ready" && dropSession(id))
          .catch((e) => isGone(e) && dropSession(id)),
      );
    void beat();
    const t = setInterval(beat, 20_000);
    return () => clearInterval(t);
  }, [readyIds, dropSession]);

  // Leaving releases the devices. Unmount (navigated off the portal in this tab) ends them
  // outright. Tab close / reload only get keepalive requests, which the api treats as "free
  // the slot in 15 s unless a heartbeat comes back" — so a reload keeps its devices.
  useEffect(() => {
    const send = (path: "leave" | "", method: "POST" | "DELETE") => {
      const token = getToken();
      for (const s of sessionsRef.current) {
        if (s.status !== "ready") continue;
        void fetch(`/v1/sessions/${s.id}${path ? `/${path}` : ""}`, {
          method,
          keepalive: true,
          headers: token ? { authorization: `Bearer ${token}` } : {},
        }).catch(() => {});
      }
    };
    const onHide = () => send("leave", "POST");
    window.addEventListener("pagehide", onHide);
    return () => {
      window.removeEventListener("pagehide", onHide);
      send("", "DELETE");
    };
  }, []);

  const value: Ctx = {
    enabled,
    sessions,
    pending,
    error,
    capacity: capacity.data ?? null,
    launch,
    end,
  };
  return <LiveDeviceCtx.Provider value={value}>{children}</LiveDeviceCtx.Provider>;
}

function streamSrc(streamPath: string, udid: string): string {
  const wsProto = window.location.protocol === "https:" ? "wss:" : "ws:";
  const ws = `${wsProto}//${window.location.host}${streamPath}?action=proxy-adb&remote=${encodeURIComponent("tcp:8886")}&udid=${encodeURIComponent(udid)}`;
  const params = new URLSearchParams({
    action: "stream",
    udid,
    player: "mse",
    ws,
  });
  return `${streamPath}#!${params.toString()}`;
}

/** ws-scrcpy puts a 52px toolbar right of the video; the bezel adds 2×10px. */
export const FRAME_CHROME = 72;
/** Video aspect (width / height) per form factor: 2560x1600 tablet, 1080x2400 phone override. */
export const FRAME_RATIO: Record<FormFactor, number> = {
  tablet: 1.6,
  phone: 0.45,
  "tablet-portrait": 0.625,
};

/**
 * The device bezel with the live stream inside. The ws-scrcpy page puts a 52px toolbar
 * (3.715rem at 14px) to the right of the video, so the frame is sized as video area +
 * toolbar + 2×10px bezel, or the video letterboxes. Phone apps run on a 1080x2400 display
 * override (9:20); the POS on the native 2560x1600 tablet (16:10); the kiosk on that tablet
 * rotated upright (10:16). Landscape frames fill their container's width; upright ones are
 * sized from `maxHeight`.
 */
export function DeviceFrame({
  frame,
  session,
  className,
  maxHeight = "min(80vh, 60rem)",
  bare = false,
}: {
  frame: FormFactor;
  session: EmulatorSessionDTO | null;
  className?: string;
  maxHeight?: string;
  /** Fill the parent. The hardware floor draws the SUNMI body around this. */
  bare?: boolean;
}) {
  const streamPath = session?.streamPath ?? null;
  const udid = session?.streamUdid ?? null;
  const isMock = !streamPath || !udid || streamPath.startsWith("/stream/mock/");
  // The scrcpy server on the device sometimes dies on a new connection (Android 14 clipboard
  // quirk in the ws-scrcpy build) and ws-scrcpy restarts it; the first viewer then sits on a
  // black frame. The stream page is same-origin (proxied), so watch its <video>: if nothing is
  // playing after a few seconds, reload the frame, up to three times.
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    setAttempt(0);
  }, [streamPath, udid, frame, session?.app]);
  useEffect(() => {
    if (isMock || attempt >= 3) return;
    const started = Date.now();
    const t = setInterval(() => {
      let playing = false;
      try {
        const v = iframeRef.current?.contentDocument?.querySelector("video");
        playing = Boolean(v && v.videoWidth > 0 && !v.paused);
      } catch {
        playing = false;
      }
      if (playing) clearInterval(t);
      else if (Date.now() - started > 9000) {
        clearInterval(t);
        setAttempt((a) => a + 1);
      }
    }, 1000);
    return () => clearInterval(t);
  }, [isMock, attempt, streamPath, udid, frame, session?.app]);
  const ratio = FRAME_RATIO[frame];
  const size: React.CSSProperties =
    frame === "tablet"
      ? {
          width: "min(100cqw, 56rem)",
          height: `calc((min(100cqw, 56rem) - ${FRAME_CHROME}px) / ${ratio} + 20px)`,
        }
      : {
          height: maxHeight,
          width: `calc((${maxHeight} - 20px) * ${ratio} + ${FRAME_CHROME}px)`,
        };
  const picture = isMock ? (
    <div className="flex h-full flex-col items-center justify-center gap-2 bg-neutral-900 text-center text-neutral-400">
      <div className="text-sm font-medium text-neutral-200">Emulator stream</div>
      <div className="max-w-xs text-xs">Local mode — the broker is mocked.</div>
    </div>
  ) : (
    <iframe
      // A phone ↔ tablet switch changes the device's display size; ws-scrcpy sizes its
      // canvas once on connect, so remount to reconnect with fresh screen info.
      key={`${frame}-${session?.app ?? ""}-${attempt}`}
      ref={iframeRef}
      title="device"
      src={streamSrc(streamPath!, udid!)}
      className="h-full w-full border-0 bg-black"
      allow="clipboard-write"
    />
  );
  if (bare) return <div className={cn("h-full w-full", className)}>{picture}</div>;
  return (
    <div
      style={{ containerType: "inline-size" }}
      className={cn("flex w-full justify-center", className)}
    >
      <div
        style={size}
        className="relative overflow-hidden rounded-[22px] border-[10px] border-neutral-900 bg-black shadow-2xl"
      >
        {picture}
      </div>
    </div>
  );
}
